import { HttpBackend, HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { EMPTY, filter, switchMap, take } from "rxjs";
import { RequestState, createRequestAction } from "ngxs-requests-plugin";
import { Action, State, StateContext } from "@ngxs/store";
import { Navigate } from "@ngxs/router-plugin";
import { fetchEventSource } from '@microsoft/fetch-event-source';

import { environment } from "../../../../environments/environment";
import { AppConfigService } from "../../services/app-config.service";
import { patchSearchIdsAndEntities } from "../../../shared/utils/patch-searchIds-and-entities";

import { LocalStorageService } from "../../services/local-storage.service";
import { AuthService } from "../../services/auth.service";

import { IFirmConsultResult } from "../../interfaces/firm.interface";

import { GetFirmConsultStream, GetFirmConsult, GetFirmConsultFail, GetFirmConsultSuccess, SearchFirm, SearchFirmNextPage, SearchFirmFail, SearchFirmSuccess, ValidateFirmIds, ValidateFirmIdsSuccess, ValidateFirmIdsFail, ValidateFirmIdsStream, ResetFirmIdsValidation, ClearSearchResults } from "./firm.actions";

export interface FirmStateModel {
  ids: string[];
  entities: {[key: string]: any};
  selectedFirmId: string;
  searchResultIds: string[];
  isLoadingFirm: boolean;
  firmIdsValidation: {valid: string[], invalid: string[]};
  isValidatingFirms: boolean;
  loadingMessage: string;
  loadingMessageIsError: boolean;
  currentSearchInput: string | null;
  currentSearchSkip: number;
}

@RequestState('getFirmConsult')
@Injectable()
export class GetFirmConsultRequestState {
}

@RequestState('searchFirm')
@Injectable()
export class SearchFirmRequestState {
}

@RequestState('validateFirmIds')
@Injectable()
export class ValidateFirmIdsRequestState {
}

@State<FirmStateModel>({
  name: 'firm',
  defaults: {
    ids: [],
    entities: {},
    selectedFirmId: null,
    searchResultIds: [],
    isLoadingFirm: false,
    firmIdsValidation: null,
    isValidatingFirms: false,
    loadingMessage: null,
    loadingMessageIsError: false,
    currentSearchInput: null,
    currentSearchSkip: 0,
  },
})
@Injectable()
export class FirmState {
  private rawHttpClient: HttpClient;
  private consultStreamAbortController: AbortController | null = null;
  private validateStreamAbortController: AbortController | null = null;

  constructor(
    private http: HttpClient,
    private rawHandler: HttpBackend,
    private localStorageService: LocalStorageService,
    private authService: AuthService,
    private injector: Injector,
    private appConfig: AppConfigService,
  ) {
    this.rawHttpClient = new HttpClient(rawHandler);
  }

  transformFirmConsult(firm: IFirmConsultResult) {
    const topPanel = firm.topPanel;
    const dataPanels = firm.tabHierarchy.reduce((acc, cur) => {
      const children = cur.children.map(child => {
        const content = firm.dataPanels.find(dataPanel => dataPanel.tag === child.tag);
        return {
          ...child,
          content
        };
      });
      cur = {
        ...cur,
        children
      };
      return [...acc, cur];
    }, []);

    return {topPanel, dataPanels};
  }

  @Action(GetFirmConsult)
  getFirmConsult(
    { dispatch, patchState }: StateContext<FirmStateModel>,
    { payload }: GetFirmConsult
  ) {
    this.consultStreamAbortController?.abort();
    this.consultStreamAbortController = null;
    patchState({loadingMessage: null, loadingMessageIsError: false, isLoadingFirm: false});

    return this.authService.currentUser$.pipe(
      filter(user => !!user),
      take(1),
      switchMap(user => {
        const lang = user?.lang || 'en';
        const params = new HttpParams({ fromObject: {dbName: this.appConfig.dbName, firm: payload.firmId, firmWithAnnualAccounts:payload.firmWithAnnualAccounts, lang: lang} });
        const request = this.http.get(environment.api + 'db/consult', {params});
        return dispatch(
          createRequestAction({
            request,
            state: GetFirmConsultRequestState,
            successAction: GetFirmConsultSuccess,
            failAction: GetFirmConsultFail,
            metadata: payload.firmId
          })
        );
      })
    );
  }

  private applyFinishedFirmConsult(
    patchState: StateContext<FirmStateModel>['patchState'],
    getState: StateContext<FirmStateModel>['getState'],
    firmId: string,
    firmData: IFirmConsultResult
  ): void {
    const firmConsult = this.transformFirmConsult(firmData);
    patchState({
      ids: [...getState().ids, firmId],
      entities: {
        ...getState().entities,
        [firmId]: {
          ...getState().entities[firmId],
          ...firmConsult,
        },
      },
      selectedFirmId: firmId,
      isLoadingFirm: false,
      loadingMessage: null,
      loadingMessageIsError: false,
    });
  }

  @Action(GetFirmConsultSuccess)
  getFirmConsultSuccess(
    { dispatch, patchState, getState }: StateContext<FirmStateModel>,
    { payload: response, metadata: firm }: GetFirmConsultSuccess
  ) {
    if (response.status === 'finished') {
      this.applyFinishedFirmConsult(patchState, getState, firm, response.content);
    } else {
      dispatch(new GetFirmConsultStream({hash: response.hash, firm}));
    }
  }

  @Action(GetFirmConsultFail)
  getFirmConsultFail(
    { patchState }: StateContext<FirmStateModel>,
    { payload: response }: GetFirmConsultFail
  ) {
    patchState({loadingMessage: response.error.error_message, loadingMessageIsError: true});
  }

  @Action(GetFirmConsultStream)
  getFirmConsultStream(
    { patchState, getState }: StateContext<FirmStateModel>,
    { payload: {hash, firm} }: GetFirmConsultStream
  ) {
    if (this.consultStreamAbortController) {
      this.consultStreamAbortController.abort();
    }
    this.consultStreamAbortController = new AbortController();
    const currentCtrl = this.consultStreamAbortController;

    const token = this.localStorageService.getItem('sequenza_token');

    patchState({isLoadingFirm: true});
    const firmId = firm;

    fetchEventSource(environment.api + 'db/consult/stream/' + hash, {
      headers: {
        "Authorization": `Bearer ${token}`
      },
      signal: currentCtrl.signal,
      onmessage: (e) => {
        if (e.event === "finished") {
          const firm = JSON.parse(e.data);
          if (!!firm) {
            this.applyFinishedFirmConsult(patchState, getState, firmId, firm);
            currentCtrl.abort();
          }
        } else if (["invalid", "failed", "error"].includes(e.event)) {
          const errorMessage = JSON.parse(e.data)?.error_message || e.data;
          patchState({isLoadingFirm: false, loadingMessage: errorMessage, loadingMessageIsError: true});
          currentCtrl.abort();
        } else if (['try-to-reschedule'].includes(e.event)) {
          const errorMessage = 'Cached extract expired, rescheduling...';
          patchState({isLoadingFirm: true, loadingMessage: errorMessage, loadingMessageIsError: false});
        }
        else {
          patchState({loadingMessage: e.data});
        }
      },
      onerror: (err) => {
        if (err.name === 'AbortError') {
          return;
        }
        patchState({isLoadingFirm: false, loadingMessage:  'Error: ' + err, loadingMessageIsError: true});
        currentCtrl.abort();
        throw err;
      }
    });
  }

  @Action(SearchFirm)
  searchFirm(
    { dispatch, patchState }: StateContext<FirmStateModel>,
    { payload: input}: SearchFirm
  ) {
    const params = new HttpParams()
      .set('highlight', 'false')
      .set('dbName', this.appConfig.dbName)
      .set('skip', '0')
      .set('q', input);
    const request = this.http.get(environment.api + 'search/firm', {params});

    patchState({
      currentSearchInput: input,
      currentSearchSkip: 0
    });

    return dispatch(
      createRequestAction({
        request,
        state: SearchFirmRequestState,
        successAction: SearchFirmSuccess,
        failAction: SearchFirmFail,
      })
    );
  }

  @Action(SearchFirmNextPage)
  searchFirmNextPage(
    { dispatch, getState, patchState }: StateContext<FirmStateModel>
  ) {
    const state = getState();
    const input = state.currentSearchInput;
    const nextSkip = state.currentSearchSkip + 40; // Assuming page size of 10 for now

    if (!input) {
      return EMPTY;
    }

    const params = new HttpParams()
      .set('highlight', 'false')
      .set('dbName', this.appConfig.dbName)
      .set('skip', nextSkip.toString())
      .set('q', input);
    const request = this.http.get(environment.api + 'search/firm', {params});

    patchState({
      currentSearchSkip: nextSkip
    });

    return dispatch(
      createRequestAction({
        request,
        state: SearchFirmRequestState,
        successAction: SearchFirmSuccess,
        failAction: SearchFirmFail,
        metadata: true,
      })
    );
  }

  @Action(SearchFirmSuccess)
  searchFirmSuccess(
    ctx: StateContext<FirmStateModel>,
    { payload: response, metadata: isNextPage }: SearchFirmSuccess
  ) {
    patchSearchIdsAndEntities(ctx, response.content, !isNextPage, 'id_firm_current');
  }

  @Action(ClearSearchResults)
  clearSearchResults(
    { patchState }: StateContext<FirmStateModel>
  ) {
    patchState({searchResultIds: []});
  }
  
  @Action(ValidateFirmIds)
  validateFirmIds(
    { dispatch, patchState }: StateContext<FirmStateModel>,
    { payload: input }
  ) {
    const body = {
      dbName: this.appConfig.dbName,
      input
    };
    const request = this.http.post(environment.api + 'db/validateFirmIdentifiers', body);
    patchState({loadingMessage: null, isValidatingFirms: true, loadingMessageIsError: false});

    return dispatch(
      createRequestAction({
        request,
        state: ValidateFirmIdsRequestState,
        successAction: ValidateFirmIdsSuccess,
        failAction: ValidateFirmIdsFail,
      })
    );
  }

  @Action(ValidateFirmIdsSuccess)
  validateFirmIdsSuccess(
    { dispatch, patchState }: StateContext<FirmStateModel>,
    { payload: response }: ValidateFirmIdsSuccess
  ) {
    if (response?.content?.invalid || response?.content?.valid) {
      patchState({firmIdsValidation: response.content, isValidatingFirms: false, loadingMessage: '', loadingMessageIsError: false})
    } else {
      dispatch(new ValidateFirmIdsStream(response.hash));
    }
  }

  @Action(ValidateFirmIdsStream)
  validateFirmIdsStream(
    { patchState }: StateContext<FirmStateModel>,
    { payload: hash }: ValidateFirmIdsStream
  ) {
    if (this.validateStreamAbortController) {
      this.validateStreamAbortController.abort();
    }
    this.validateStreamAbortController = new AbortController();
    const currentCtrl = this.validateStreamAbortController;

    const token = this.localStorageService.getItem('sequenza_token');
    
    fetchEventSource(environment.api + 'db/validateFirmIdentifiers/stream/' + hash, {
      headers: {
        "Authorization": `Bearer ${token}`
      },
      signal: currentCtrl.signal,
      onmessage: (e) => {
        if (["assigned", "queued", "updated", "update", "timedout"].includes(e.event)) {
          patchState({loadingMessage: e.data});
        } else if (e.event === "finished") {
          const response = JSON.parse(e.data);

          if (!!response) {
            patchState({firmIdsValidation: response, isValidatingFirms: false});
            currentCtrl.abort();
          }
        } else if (['try-to-reschedule'].includes(e.event)) {
          const errorMessage = 'Cached firm identifiers expired, rescheduling...';
          patchState({loadingMessage: errorMessage, loadingMessageIsError: false, isValidatingFirms: true});
        }
        else if (["invalid", "failed", "error"].includes(e.event)) {
          const errorMessage = JSON.parse(e.data)?.error_message || e.data;
          patchState({loadingMessage: errorMessage, loadingMessageIsError: true, isValidatingFirms: false});
          currentCtrl.abort();
        }
      },
      onerror: (err) => {
        if (err.name === 'AbortError') {
          return;
        }
        patchState({loadingMessage: 'Error: ' + err, isValidatingFirms: false, loadingMessageIsError: true});
        currentCtrl.abort();
        throw err;
      }
    });
  }

  @Action(ValidateFirmIdsFail)
  validateFirmIdsFail(
    { patchState }: StateContext<FirmStateModel>,
    { payload }: any
  ) {
    const message = payload?.error?.content || 'Error';
    patchState({loadingMessage: message, isValidatingFirms: false, loadingMessageIsError: true});
  }

  @Action(ResetFirmIdsValidation)
  resetFirmIdsValidation(
    { patchState }: StateContext<FirmStateModel>
  ) {
    patchState({firmIdsValidation: null, isValidatingFirms: false, loadingMessage: '', loadingMessageIsError: false});
  }
}
